import { useRef, useState } from 'react'
import { useStripe, useElements } from '@stripe/react-stripe-js'
import { useToastV2 } from '@easyllama/ui-components'
import { useApp } from '~/context/appContext'
import { createPaymentMethod } from '~/service/Billing/fetchApiClient'

export const useSubmitPaymentMethod = ({ onSuccess, onSetupIntentConsumed }) => {
  const toast = useToastV2()
  const stripe = useStripe()
  const elements = useElements()
  const { company, jwt, csrf } = useApp()
  const [isSubmitting, setIsSubmitting] = useState(false)
  // Set when confirmSetup succeeds but the backend save fails. The SetupIntent is
  // single-use and already consumed at that point, but the PaymentMethod ID it
  // produced is still a valid, reusable Stripe object - so the next Submit click
  // retries just the save instead of forcing a fresh SetupIntent and card re-entry.
  const retryPaymentMethodIdRef = useRef(null)

  const handleSubmit = async () => {
    if (retryPaymentMethodIdRef.current) {
      setIsSubmitting(true)
      try {
        await createPaymentMethod(company.id, retryPaymentMethodIdRef.current, jwt, csrf)
        retryPaymentMethodIdRef.current = null
        onSuccess?.()
      } catch (err) {
        retryPaymentMethodIdRef.current = null
        toast.critical(
          err?.message || 'Your card was verified but we could not save it. Please try again.',
          undefined,
          { showCloseButton: false }
        )
        onSetupIntentConsumed?.()
      } finally {
        setIsSubmitting(false)
      }
      return
    }

    if (!stripe || !elements) return

    setIsSubmitting(true)
    try {
      const { error: submitError } = await elements.submit()
      if (submitError) {
        toast.critical(submitError?.message || 'Failed to submit payment details', undefined, { showCloseButton: false })
        return
      }

      const { setupIntent, error } = await stripe.confirmSetup({
        elements,
        confirmParams: { return_url: window.location.href },
        redirect: 'if_required',
      })

      if (error) {
        toast.critical(error?.message || 'Failed to submit payment details', undefined, { showCloseButton: false })
        return
      }

      if (!setupIntent?.payment_method) {
        toast.critical('Failed to submit payment details', undefined, { showCloseButton: false })
        return
      }

      try {
        await createPaymentMethod(company.id, setupIntent.payment_method, jwt, csrf)
      } catch (err) {
        retryPaymentMethodIdRef.current = setupIntent.payment_method
        toast.critical(
          err?.message || 'Your card was verified but we could not save it. Please try again.',
          undefined,
          { showCloseButton: false }
        )
        return
      }

      onSuccess?.()
    } catch (err) {
      toast.critical(err?.message || 'Failed to submit payment details', undefined, { showCloseButton: false })
    } finally {
      setIsSubmitting(false)
    }
  }

  return { handleSubmit, isSubmitting, stripe }
}
